import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import DefaultLayout from '@/layouts/Default/default';
import Link from '@/components/common/Link';
import BlogPostHeader from '@/components/Blog/BlogPostHeader';
import BlogPostSidebar from '@/components/Blog/BlogPostSidebar';
import { BlogContentRenderer } from '@/components/Blog/BlogComponents';
import { getPostBySlug } from '@/data/blogPosts';
import { Seo } from '@/components/SEO/Seo';
import { siteMetadata } from '@/config/seo';

const BlogPostPage: FC = () => {
  const params = useParams<{ slug: string }>();
  const slugParam = params.slug ??
    (typeof window !== 'undefined'
      ? window.location.pathname.replace(/^\/blog\//, '').replace(/\/$/, '')
      : undefined);

  const post = useMemo(() => {
    if (!slugParam) return undefined;
    return getPostBySlug(slugParam);
  }, [slugParam]);

  const publishedIso = useMemo(() => {
    if (!post?.date) return undefined;
    const parsed = new Date(post.date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
  }, [post?.date]);

  if (!post) {
    return (
      <DefaultLayout hideHeader>
        <div className="flex min-h-screen flex-col items-center justify-center bg-white px-6 text-center dark:bg-zinc-950">
          <h1 className="mb-4 text-3xl font-semibold text-zinc-900 dark:text-white">
            Post Not Found
          </h1>
          <p className="mb-8 max-w-xl text-zinc-600 dark:text-zinc-400">
            The blog post you&apos;re looking for doesn&apos;t exist or has been moved.
          </p>
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-zinc-700 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-zinc-200"
          >
            ← Back to Blog
          </Link>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout hideHeader>
      <Seo
        title={post.title}
        description={post.excerpt}
        canonicalPath={`/blog/${post.slug}`}
        image={post.imageUrl}
        imageAlt={post.title}
        type="article"
        keywords={[post.category, 'Turbo Labs blog', 'AI insights']}
        publishedTime={publishedIso}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: post.title,
          description: post.excerpt,
          image: post.imageUrl,
          datePublished: publishedIso,
          author: {
            '@type': 'Person',
            name: post.author.name,
          },
          publisher: {
            '@type': 'Organization',
            name: siteMetadata.siteName,
            logo: {
              '@type': 'ImageObject',
              url: `${siteMetadata.siteUrl}/turbo-circle.svg`,
            },
          },
          mainEntityOfPage: `${siteMetadata.siteUrl}/blog/${post.slug}`,
        }}
      />
      <div className="min-h-screen bg-white dark:bg-zinc-950">
        <div className="container mx-auto max-w-[97%] px-4 pt-24">
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 transition-colors hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400"
          >
            ← Back to Blog
          </Link>
        </div>

        <div className="container mx-auto max-w-[97%] px-4 pt-4">
          <BlogPostHeader post={post} />
        </div>

        <div className="container mx-auto px-4 pb-20">
          <div className="flex flex-col gap-12 lg:flex-row">
            <div className="order-2 w-full lg:order-1 lg:w-1/4">
              <BlogPostSidebar post={post} />
            </div>
            <div className="order-1 w-full lg:order-2 lg:w-3/4">
              {post.content && (
                <BlogContentRenderer
                  content={post.content}
                  postTitle={post.title}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default BlogPostPage;
